import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

// Use sessionStorage so the admin token does not persist across browser sessions.
const TOKEN_KEY = "twtg_admin_token";

export default function AdminSubscribers() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/subscribers", { params: { token: t } });
      setData(data);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load");
      setData(null);
      sessionStorage.removeItem(TOKEN_KEY);
    } finally { setLoading(false); }
  }, []);

  // Auto-load once on mount if a token was previously saved in this session.
  useEffect(() => {
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (saved) load(saved);
  }, [load]);

  const updateName = async (id, newName) => {
    try {
      const { data: updated } = await api.patch(`/admin/subscribers/${id}`, { name: newName || null }, { params: { token } });
      setData(d => ({ ...d, subscribers: d.subscribers.map(s => s.id === id ? updated : s) }));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not update");
    }
  };

  const exportCsv = () => {
    if (!data?.subscribers?.length) return;
    const rows = [["email", "name", "joined"]].concat(
      data.subscribers.map(s => [s.email, s.name || "", s.created_at])
    );
    const csv = rows.map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `subscribers-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-subscribers-page" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="overline text-olive mb-4">Admin</div>
      <h1 className="font-serif text-5xl md:text-6xl mb-10">Subscribers</h1>

      <div className="flex items-end gap-3 mb-10 max-w-xl">
        <label className="flex-1 flex flex-col gap-2">
          <span className="overline text-muted">Admin token</span>
          <input type="password" value={token} onChange={e => setToken(e.target.value)} className="border border-line bg-surface px-4 py-3 focus:border-olive outline-none" placeholder="Paste your ADMIN_TOKEN…" data-testid="admin-token-input"/>
        </label>
        <button onClick={() => load(token)} disabled={!token || loading} className="btn-primary" data-testid="admin-load-btn">{loading ? "…" : "Load"}</button>
      </div>

      {error && <div className="text-clay border border-clay px-5 py-3 mb-6">{error}</div>}

      {data && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="overline text-muted" data-testid="admin-count">{data.count.toLocaleString()} subscribers</div>
            <button onClick={exportCsv} className="btn-outline" data-testid="admin-export-csv">Export CSV</button>
          </div>
          <div className="border border-line">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-line bg-alt overline text-muted">
              <div className="col-span-6">Email</div>
              <div className="col-span-3">Name (click to edit)</div>
              <div className="col-span-3 text-right">Joined</div>
            </div>
            {data.subscribers.map(s => (
              <div key={s.id} className="grid grid-cols-12 px-5 py-3 border-b border-line text-sm items-center" data-testid={`admin-sub-${s.id}`}>
                <div className="col-span-6 truncate">{s.email}</div>
                <div className="col-span-3">
                  <input
                    defaultValue={s.name || ""}
                    placeholder="—"
                    onBlur={(e) => { const v = e.target.value.trim(); if (v !== (s.name || "")) updateName(s.id, v); }}
                    onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                    className="w-full bg-transparent border border-transparent hover:border-line focus:border-olive px-2 py-1 outline-none text-sm"
                    data-testid={`admin-sub-name-${s.id}`}
                  />
                </div>
                <div className="col-span-3 text-right text-muted">{new Date(s.created_at).toLocaleDateString()}</div>
              </div>
            ))}
            {data.subscribers.length === 0 && <div className="px-5 py-8 text-muted text-center">No subscribers yet.</div>}
          </div>
        </>
      )}
    </main>
  );
}
